import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    View,
    Text,
    FlatList,
    RefreshControl,
    StyleSheet,
    ActivityIndicator,
    TextInput,
    TouchableOpacity,
} from 'react-native';
import SimpleHeader from '../../components/SimpleHeader';
import { COLORS } from '../../theme/themeTokens';
import { useAuth } from '../../contexts/AuthContext';
import { v2ListSuperUserSessions } from '../../services/v2/api';
import type { V2UserSessionDTO } from '../../services/v2/api';

interface UserSessionsScreenProps {
    onBack: () => void;
}

const PAGE_SIZE = 50;

function formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || seconds < 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || h > 0) parts.push(`${m}m`);
    parts.push(`${s}s`);
    return parts.join(' ');
}

function formatStartedAt(iso: string): string {
    const d = new Date(iso);
    // Local TZ — admin browsing on mobile expects to see "what time was it for them".
    const date = d.toLocaleDateString();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time}`;
}

const UserSessionsScreen: React.FC<UserSessionsScreenProps> = ({ onBack }) => {
    const { user } = useAuth();
    const [sessions, setSessions] = useState<V2UserSessionDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');

    // Client-side filter on the already-loaded page. Backend supports only
    // user_id filter, not login substring — and we don't have a user_id at
    // hand on mobile. Searching the latest 50 sessions by login covers the
    // typical "who's online right now" use case; for cross-history search,
    // OneAdmin web has full-table filters.
    const filteredSessions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return sessions;
        return sessions.filter(s => {
            const login = (s.user_login ?? '').toLowerCase();
            if (login.includes(q)) return true;
            return s.user_id.toLowerCase().includes(q);
        });
    }, [sessions, query]);

    const load = useCallback(async (mode: 'initial' | 'refresh') => {
        if (mode === 'initial') setLoading(true);
        if (mode === 'refresh') setRefreshing(true);
        setError(null);
        try {
            const res = await v2ListSuperUserSessions({
                sort: 'started_at',
                order: 'desc',
                limit: PAGE_SIZE,
            });
            setSessions(res.sessions ?? []);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to load sessions';
            setError(msg);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load('initial');
    }, [load]);

    // Defensive: route is gated in App.tsx + ProfileScreen, but guard against
    // direct navigation in case is_super_user flipped off mid-session.
    if (user?.is_super_user !== true) {
        return (
            <View style={styles.container}>
                <SimpleHeader title="User Sessions" onBack={onBack} />
                <View style={styles.center}>
                    <Text style={styles.errorText}>Super-user privilege required.</Text>
                </View>
            </View>
        );
    }

    const renderItem = ({ item }: { item: V2UserSessionDTO }) => {
        const isActive = item.ended_at == null;
        return (
            <View style={styles.card}>
                <View style={styles.cardHeader}>
                    <Text style={styles.login} numberOfLines={1}>
                        {item.user_login ?? item.user_id.slice(0, 8)}
                    </Text>
                    {isActive ? (
                        <View style={styles.activeChip}>
                            <Text style={styles.activeChipText}>active</Text>
                        </View>
                    ) : item.end_reason ? (
                        <View style={styles.reasonChip}>
                            <Text style={styles.reasonChipText}>{item.end_reason}</Text>
                        </View>
                    ) : null}
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Started</Text>
                    <Text style={styles.value}>{formatStartedAt(item.started_at)}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Duration</Text>
                    <Text style={styles.value}>
                        {item.duration_seconds != null
                            ? formatDuration(item.duration_seconds)
                            : isActive
                                ? formatDuration(Math.round((Date.now() - new Date(item.started_at).getTime()) / 1000)) + ' (live)'
                                : '—'}
                    </Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Device</Text>
                    <Text style={styles.value} numberOfLines={1}>
                        {[item.device_model, item.device_os_version].filter(Boolean).join(' · ') || '—'}
                    </Text>
                </View>

                {item.app_version ? (
                    <View style={styles.row}>
                        <Text style={styles.label}>App</Text>
                        <Text style={styles.value}>{item.app_version}</Text>
                    </View>
                ) : null}
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <SimpleHeader title="User Sessions" onBack={onBack} />

            <View style={styles.searchWrapper}>
                <TextInput
                    style={styles.searchInput}
                    placeholder="Search by login…"
                    placeholderTextColor="#95a5a6"
                    value={query}
                    onChangeText={setQuery}
                    autoCapitalize="none"
                    autoCorrect={false}
                    returnKeyType="search"
                />
                {query.length > 0 && (
                    <TouchableOpacity
                        style={styles.searchClear}
                        onPress={() => setQuery('')}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                        <Text style={styles.searchClearText}>×</Text>
                    </TouchableOpacity>
                )}
            </View>

            {loading && sessions.length === 0 ? (
                <View style={styles.center}>
                    <ActivityIndicator size="large" color={COLORS.primaryNavy} />
                </View>
            ) : (
                <FlatList
                    data={filteredSessions}
                    keyExtractor={(s) => s.id}
                    renderItem={renderItem}
                    contentContainerStyle={styles.listContent}
                    keyboardShouldPersistTaps="handled"
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={() => load('refresh')}
                            tintColor={COLORS.primaryNavy}
                        />
                    }
                    ListEmptyComponent={
                        error ? (
                            <View style={styles.center}>
                                <Text style={styles.errorText}>{error}</Text>
                            </View>
                        ) : query ? (
                            <View style={styles.center}>
                                <Text style={styles.emptyText}>No sessions match "{query}".</Text>
                                <Text style={styles.emptyHint}>Tip: search runs over the last {PAGE_SIZE} sessions.{'\n'}For full-history search use OneAdmin web.</Text>
                            </View>
                        ) : (
                            <View style={styles.center}>
                                <Text style={styles.emptyText}>No sessions found.</Text>
                            </View>
                        )
                    }
                    ListHeaderComponent={
                        error && sessions.length > 0 ? (
                            <View style={styles.banner}>
                                <Text style={styles.bannerText}>{error}</Text>
                            </View>
                        ) : null
                    }
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f5f6f8',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        minHeight: 200,
    },
    errorText: {
        color: '#c0392b',
        fontSize: 16,
        textAlign: 'center',
    },
    emptyText: {
        color: '#7f8c8d',
        fontSize: 16,
        textAlign: 'center',
    },
    emptyHint: {
        marginTop: 8,
        color: '#95a5a6',
        fontSize: 14,
        textAlign: 'center',
        lineHeight: 18,
    },
    searchWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 16,
        marginTop: 12,
        marginBottom: 4,
        paddingHorizontal: 12,
        backgroundColor: '#fff',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#e1e4e8',
    },
    searchInput: {
        flex: 1,
        height: 40,
        fontSize: 16,
        color: '#1a1a1a',
        padding: 0,
    },
    searchClear: {
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        backgroundColor: '#ecf0f1',
    },
    searchClearText: {
        fontSize: 18,
        lineHeight: 20,
        color: '#7f8c8d',
        marginTop: -2,
    },
    banner: {
        marginHorizontal: 16,
        marginTop: 12,
        padding: 10,
        borderRadius: 8,
        backgroundColor: 'rgba(192, 57, 43, 0.08)',
        borderColor: 'rgba(192, 57, 43, 0.4)',
        borderWidth: 1,
    },
    bannerText: {
        color: '#c0392b',
        fontSize: 15,
    },
    listContent: {
        padding: 16,
        paddingBottom: 32,
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOpacity: 0.04,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
        elevation: 1,
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    login: {
        flex: 1,
        marginRight: 8,
        fontSize: 17,
        fontWeight: '600',
        color: '#1a1a1a',
    },
    activeChip: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        backgroundColor: 'rgba(46, 204, 113, 0.12)',
        borderColor: 'rgba(46, 204, 113, 0.5)',
        borderWidth: 1,
    },
    activeChipText: {
        color: '#27ae60',
        fontSize: 13,
        fontWeight: '600',
    },
    reasonChip: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        backgroundColor: '#ecf0f1',
        borderColor: '#bdc3c7',
        borderWidth: 1,
    },
    reasonChipText: {
        color: '#34495e',
        fontSize: 13,
        fontWeight: '500',
    },
    row: {
        flexDirection: 'row',
        marginTop: 4,
    },
    label: {
        width: 80,
        color: '#7f8c8d',
        fontSize: 15,
    },
    value: {
        flex: 1,
        color: '#2c3e50',
        fontSize: 15,
    },
});

export default UserSessionsScreen;
